/**
 * Helpers for the per-parade-year "rolling in next parade" status on a SubKrewe.
 *
 * Storage shape (new): `SubKrewe.rolling_next_parade_by_year` is a map from a
 * parade year (string, e.g. "2026") to one of 'Yes' | 'No' | 'Unknown'.
 *
 * Legacy shape: some documents still carry the scalar `rolling_next_parade`.
 * That scalar represents the captain's intent for whatever parade year was
 * active when it was last set, so it is NOT auto-applied to other years —
 * unset years resolve to 'Unknown' and require an explicit captain answer.
 */

export type RollingStatus = 'Yes' | 'No' | 'Unknown'
export type RollingStatusByYear = Record<string, RollingStatus>

export interface SubKreweRollingLike {
  rolling_next_parade?: RollingStatus | string | null
  rolling_next_parade_by_year?: RollingStatusByYear | null
}

/**
 * Resolve the rolling status for a given parade year.
 * Reads only from the per-year map. Returns undefined when nothing is set
 * for that year — the legacy scalar is intentionally not used as a fallback
 * because it represents a previous year's intent.
 */
export function getRollingStatusForYear(
  sk: SubKreweRollingLike | null | undefined,
  year: string | number | null | undefined,
): RollingStatus | undefined {
  if (!sk) return undefined
  const map = sk.rolling_next_parade_by_year
  if (map && year !== null && year !== undefined) {
    const v = map[String(year)]
    if (v === 'Yes' || v === 'No' || v === 'Unknown') return v
  }
  return undefined
}

/**
 * Same as {@link getRollingStatusForYear} but returns 'Unknown' instead of
 * undefined when nothing is set. Convenient for UI labels / badges.
 */
export function getRollingStatusForYearOrUnknown(
  sk: SubKreweRollingLike | null | undefined,
  year: string | number | null | undefined,
): RollingStatus {
  return getRollingStatusForYear(sk, year) ?? 'Unknown'
}

/**
 * Build a new per-year map with `value` merged under `year`, preserving any
 * other years already present.
 */
export function mergeRollingStatusForYear(
  sk: SubKreweRollingLike | null | undefined,
  year: string | number,
  value: RollingStatus,
): RollingStatusByYear {
  const existing = sk?.rolling_next_parade_by_year
  const base: RollingStatusByYear = existing ? { ...existing } : {}
  base[String(year)] = value
  return base
}
